import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useControllableState } from '@radix-ui/react-use-controllable-state'

type UseLongTextValueStateProps = {
    textValue?: string
    maxLines?: number
    providedShowFullText?: boolean
    providedSetShowFullText?: (showFullText: boolean) => void
}

export function useLongTextValueState({
    textValue,
    maxLines,
    providedShowFullText,
    providedSetShowFullText,
}: UseLongTextValueStateProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)

    const [isTruncated, setIsTruncated] = useState(false)
    useLayoutEffect(() => {
        if (!maxLines) return

        const wrapper = wrapperRef.current
        if (!wrapper) return

        function checkTruncation() {
            if (!wrapper) return

            const isTruncated = wrapper.scrollHeight > wrapper.clientHeight
            setIsTruncated(isTruncated)
        }

        checkTruncation()

        const resizeObserver = new ResizeObserver(checkTruncation)
        resizeObserver.observe(wrapper)

        return () => {
            resizeObserver.disconnect()
        }
    }, [maxLines])

    const [showFullText, setShowFullText] = useControllableState<boolean>({
        defaultProp: false,
        prop: providedShowFullText,
        onChange: providedSetShowFullText,
    })
    const setShowFullTextRef = useRef(setShowFullText)
    setShowFullTextRef.current = setShowFullText

    const toggleShowFullText = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault()
            e.stopPropagation()

            setShowFullText((prev) => !prev)
        },
        [setShowFullText]
    )

    // Reset `showFullText` when value changes.
    useLayoutEffect(() => {
        setShowFullTextRef.current(false)
    }, [textValue])

    return useMemo(
        () => ({
            wrapperRef,
            showFullText: !!showFullText,
            isTruncated,
            toggleShowFullText,
        }),
        [isTruncated, showFullText, toggleShowFullText]
    )
}
